/*
 * Cosmora sticky buy bar (snippets/cosmora-sticky-buy-bar.liquid).
 *
 * Visibility: shown only after the shopper has scrolled past the main Add to
 * cart button (its bottom edge is above the top of the viewport). Hidden on
 * first load and whenever the button is on screen or still below the fold
 * (owner decision 2026-09-15: not visible before scrolling). Also hidden while
 * the footer is on screen or
 * Shopify's cookie consent banner is showing (both sit at the bottom edge; the
 * bar returns once the banner is dismissed). Computed from live element
 * positions on scroll/resize (rAF-throttled), not from IntersectionObserver
 * alone: an observer only reports intersection *changes*, so a fast fling,
 * iOS tap-to-top or anchor jump could leave the bar in a stale state.
 * Observers are kept only as extra triggers for layout shifts.
 *
 * Add to cart: the CTA is a native submit button bound to the main product
 * form (form="product-form-<section>"), so product-form.js handles it exactly
 * like the main button, with the same hidden variant id and quantity. It is
 * disabled whenever the main button is disabled/aria-disabled (variant switch
 * in flight, add in flight, sold out), so it can never submit a stale variant.
 * product-info.js re-enables the main button if a variant render fails, and
 * the attribute observer carries that back here.
 *
 * Variant sync: on PUB_SUB_EVENTS.variantChange for its section, copies the
 * thumbnail and name/price markup from the freshly rendered section HTML.
 */
if (!customElements.get('cosmora-sticky-buy-bar')) {
  customElements.define(
    'cosmora-sticky-buy-bar',
    class CosmoraStickyBuyBar extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.form = document.getElementById(this.dataset.formId);
        this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.cta = this.querySelector('[data-sticky-cta]');
        if (!this.form || !this.mainButton || !this.cta) return;

        this.footer = document.querySelector('footer.footer');
        this.frameRequested = false;
        this.requestUpdate = this.requestUpdate.bind(this);

        window.addEventListener('scroll', this.requestUpdate, { passive: true });
        window.addEventListener('resize', this.requestUpdate, { passive: true });
        window.visualViewport?.addEventListener('resize', this.requestUpdate, { passive: true });

        if ('IntersectionObserver' in window) {
          this.positionObserver = new IntersectionObserver(this.requestUpdate);
          this.positionObserver.observe(this.mainButton);
          if (this.footer) this.positionObserver.observe(this.footer);
        }

        // The consent banner is injected, restyled and removed/hidden by Shopify's script at any
        // time after load; watch the body's direct children and the banner's own attributes.
        this.consentObserver = new MutationObserver(this.requestUpdate);
        this.consentObserver.observe(document.body, { childList: true });
        this.consentAttributeObserver = new MutationObserver(this.requestUpdate);

        this.syncDisabledState();
        this.mainButtonAttributeObserver = new MutationObserver(() => this.syncDisabledState());
        this.mainButtonAttributeObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
        });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) =>
            this.onVariantChange(event)
          );
        }

        this.updateVisibility();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.requestUpdate);
        window.removeEventListener('resize', this.requestUpdate);
        window.visualViewport?.removeEventListener('resize', this.requestUpdate);
        this.positionObserver?.disconnect();
        this.consentObserver?.disconnect();
        this.consentAttributeObserver?.disconnect();
        this.mainButtonAttributeObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
      }

      requestUpdate() {
        if (this.frameRequested) return;
        this.frameRequested = true;
        requestAnimationFrame(() => {
          this.frameRequested = false;
          this.updateVisibility();
        });
      }

      consentBannerVisible(viewportHeight) {
        const banner = document.getElementById('shopify-pc__banner');
        if (banner !== this.observedBanner) {
          this.consentAttributeObserver.disconnect();
          this.observedBanner = banner;
          if (banner) {
            this.consentAttributeObserver.observe(banner, {
              attributes: true,
              attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
            });
          }
        }
        if (!banner || !banner.isConnected) return false;

        const style = window.getComputedStyle(banner);
        if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
        const rect = banner.getBoundingClientRect();
        return rect.height > 0 && rect.top < viewportHeight && rect.bottom > 0;
      }

      updateVisibility() {
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const buttonRect = this.mainButton.getBoundingClientRect();
        const scrolledPastMainButton = buttonRect.bottom <= 0;
        const footerInView = this.footer ? this.footer.getBoundingClientRect().top < viewportHeight : false;
        const visible = scrolledPastMainButton && !footerInView && !this.consentBannerVisible(viewportHeight);

        if (visible === this.classList.contains('is-visible')) return;
        this.classList.toggle('is-visible', visible);
        this.toggleAttribute('inert', !visible);
        this.setAttribute('aria-hidden', String(!visible));
      }

      syncDisabledState() {
        this.cta.disabled =
          this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true';
      }

      onVariantChange(event) {
        const data = event && event.data;
        if (!data || data.sectionId !== this.sectionId || !data.html) return;

        const fresh = data.html.querySelector('cosmora-sticky-buy-bar');
        if (!fresh) return;

        ['[data-sticky-media]', '[data-sticky-name]'].forEach((selector) => {
          const source = fresh.querySelector(selector);
          const target = this.querySelector(selector);
          if (source && target) target.innerHTML = source.innerHTML;
        });
      }
    }
  );
}
